import React, { useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomModelsCRUD = ({ config, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  
  const customModels = config?.custom_models || {};
  const features = customModels.features || [];
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '⚡'
  });



  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: '⚡'
    });
    setEditingIndex(-1);
    setShowAddForm(false);
  };

  const handleEdit = (index) => {
    const feature = features[index];
    setFormData({
      title: feature.title || '',
      description: feature.description || '',
      icon: feature.icon || '⚡'
    });
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Feature title is required');
      return;
    }

    setLoading(true);
    try {
      let updatedFeatures = [...features];
      
      if (editingIndex >= 0) {
        // Update existing
        updatedFeatures[editingIndex] = { ...formData };
      } else {
        // Add new
        updatedFeatures.push({ ...formData });
      }

      await axios.put(`${API_BASE_URL}/api/admin/config/custom_models/features`, {
        value: updatedFeatures,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      resetForm();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000); // Clear status after 3 seconds
    } catch (error) {
      console.error('Error saving feature:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000); // Clear status after 3 seconds
    }
    setLoading(false);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;

    setLoading(true);
    try {
      const updatedFeatures = features.filter((_, i) => i !== index);
      
      await axios.put(`${API_BASE_URL}/api/admin/config/custom_models/features`, {
        value: updatedFeatures,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      alert('Feature deleted successfully!');
    } catch (error) {
      console.error('Error deleting feature:', error);
      alert('Error deleting feature');
    }
    setLoading(false);
  };

  const updateTextFields = async (field, value) => {
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/config/custom_models/${field}`, {
        value: value,
        data_type: 'string'
      }, {
        headers: getAuthHeaders()
      });
      await onUpdate();
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Error updating field');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-8">
      {/* Section Title & Description */}
      <div className="bg-purple-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Content</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
            <input
              type="text"
              value={customModels.title || ''}
              onChange={(e) => updateTextFields('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Custom AI Solutions"
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
            <textarea
              value={customModels.description || ''}
              onChange={(e) => updateTextFields('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              rows={4}
              placeholder="Need something more specific? We develop custom Small Language Models..."
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Features Management */}
      <div className="bg-white p-6 rounded-lg border">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Custom Model Features</h3>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            disabled={loading}
          >
            Add Feature
          </button>
        </div>

        {/* Features List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {features.map((feature, index) => (
            <div key={`custom-model-${feature.title}-${index}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="text-2xl">{feature.icon}</div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </div>
          ))}
          
          {features.length === 0 && (
            <div className="col-span-full">
              <p className="text-gray-500 text-center py-8">No features added yet.</p>
            </div>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold mb-4">
              {editingIndex >= 0 ? 'Edit Feature' : 'Add New Feature'}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Feature Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Domain-Specific SLMs"
                  disabled={loading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon (Emoji)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="⚡"
                  disabled={loading}
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Feature Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Lightweight models trained on your specific mortgage data and processes"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingIndex >= 0 ? 'Update' : 'Add')} Feature
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
        <div className="bg-gradient-to-br from-primary-900 to-primary-800 rounded-lg p-8 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">
              {customModels.title || "Custom AI Solutions"}
            </h2>
            <p className="text-primary-100 max-w-3xl mx-auto">
              {customModels.description || "Need something more specific? We develop custom Small Language Models..."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.slice(0, 6).map((feature, index) => (
              <div key={`custom-model-preview-${feature.title}-${index}`} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4">
                <div className="text-2xl mb-3">{feature.icon}</div>
                <h4 className="font-semibold mb-2">{feature.title}</h4>
                <p className="text-sm text-primary-100">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Save/Discard buttons */}
        {hasUnsavedChanges && (
          <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3"></div>
              <span className="text-sm text-yellow-800">You have unsaved changes</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setHasUnsavedChanges(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Content</span>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Save status message */}
        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            Content saved successfully!
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            Error saving content. Please try again.
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomModelsCRUD;